import i18n from '@/utils/i18n';
import InvoiceBadgedIcon from '@assets/icons/invoice-badged.svg';
import InvoiceIcon from '@assets/icons/invoice.svg';
import React, { useMemo } from 'react';
import { CheckInLabel } from '@/components/check-in-label';
import { ConsumptionTax } from '@/contexts/dynamic-config';
import { displayInvoiceTotal } from '@/utils/invoice';
import { formatDate } from '@/utils/date';
import { getOrderDate, isPastDateOrToday } from '@/utils/order-helpers';
import { HistoryListItem } from '@/components/history-list-item';
import { OrderInvoiceFieldsFragment } from '@/types';
import { Platform } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  consumptionTax?: ConsumptionTax[] | null;
  invoice: OrderInvoiceFieldsFragment;
  onPress: (invoiceUUID: string) => void;
}

export const ExternalOrderHistoryListItem: React.FC<Props> = ({
  consumptionTax,
  onPress,
  invoice,
}) => {
  const theme = useTheme();
  const { currencyCode } = usePaymentsContext();
  const checkInConfig = useDynamicConfigValue('check_in_config');

  const InvoiceIconComponent = invoice.seen ? InvoiceIcon : InvoiceBadgedIcon;

  // Since invoice balances may be zero, sum the invoice line items to
  // determine what the original balance likely was. This is not guaranteed
  // to be accurate, but looks better than a bunch of $0 orders.
  const priceText = useMemo(
    () =>
      displayInvoiceTotal(
        invoice.order_invoice_line_items,
        currencyCode,
        consumptionTax,
      ),
    [consumptionTax, currencyCode, invoice.order_invoice_line_items],
  );

  const isTodayOrPastInvoice = useMemo(() => {
    const invoiceDate = getOrderDate(invoice);
    return isPastDateOrToday(invoiceDate);
  }, [invoice]);

  const existingCheckIn = invoice.order_invoice_check_in;

  const showCheckInStatusLabel =
    isTodayOrPastInvoice &&
    Platform.OS !== 'web' &&
    checkInConfig?.check_in_enabled;

  return (
    <HistoryListItem
      bottomLeftComponent={
        showCheckInStatusLabel ? (
          <CheckInLabel existingCheckIn={existingCheckIn} />
        ) : null
      }
      bottomRightText={
        invoice
          ? i18n.t('invoice_number', {
              number: invoice.invoice_number,
            })
          : undefined
      }
      onPress={() => onPress(invoice.uuid)}
      showTopRightIcon
      testID="external-order-list-item"
      topLeftText={formatDate(invoice.invoice_date) || ''}
      topRightIcon={<InvoiceIconComponent accentFill={theme.colors.accent} />}
      topRightText={priceText}
    />
  );
};
